import { useState, useCallback } from 'react';
import { ToastQueue } from '@adobe/react-spectrum';
import { deleteMilestones } from '../../../services/momentsApi';

interface UseDeleteMilestonesOptions {
  issueId?: string;
  /** Prefer DELETE response body to skip follow-up GETs when envelope is present */
  onRefresh: (patchResponse?: unknown) => Promise<void>;
}

interface DeleteMilestonesParams {
  phaseId?: string;
}

export const useDeleteMilestones = ({
  issueId,
  onRefresh
}: UseDeleteMilestonesOptions) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteMilestonesHandler = useCallback(
    async ({ phaseId }: DeleteMilestonesParams): Promise<boolean> => {
      if (!issueId) {
        ToastQueue.negative('Missing issue ID', { timeout: 3000 });
        return false;
      }

      setIsDeleting(true);

      try {
        const result = await deleteMilestones(issueId, phaseId);

        if (!result.success) {
          if (result.alreadyNotified) return false;
          throw new Error(result.message || 'Failed to delete milestones');
        }

        await onRefresh(result.data);
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to delete milestones';
        ToastQueue.negative(errorMessage, { timeout: 5000 });
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [issueId, onRefresh]
  );

  return { deleteMilestonesHandler, isDeleting };
};
